import React, { useMemo } from 'react';
import { Box, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useCalendar } from '../api';
import { useHftStore } from '../store';
import type { DaySummary } from '../types';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SEGMENTS = ['nse_eq', 'nse_fut', 'nse_idx', 'nse_opt'];
const SEG_SHORT: Record<string, string> = { nse_eq: 'EQ', nse_fut: 'FT', nse_idx: 'IX', nse_opt: 'OP' };
const SEG_COLORS: Record<string, string> = {
  nse_eq: '#3b82f6',
  nse_fut: '#f59e0b',
  nse_idx: '#10b981',
  nse_opt: '#ef4444',
};

type CellStatus = 'complete' | 'partial' | 'gap' | 'holiday' | 'weekend' | 'future';

function cellStatus(day: DaySummary | undefined, dateStr: string): CellStatus {
  if (!day) return 'future';
  if (!day.trading_day) return day.layers && Object.keys(day.layers).length > 0 ? 'holiday' : 'weekend';
  const layers = day.layers || {};
  const hasB = (layers['bronze'] || 0) > 0;
  const hasS = (layers['silver'] || 0) > 0;
  const hasG = (layers['gold'] || 0) > 0;
  if (hasS && hasG) return 'complete';
  if (hasB || hasS) return 'partial';
  return 'gap';
}

const STATUS_BG: Record<CellStatus, Record<string, string>> = {
  complete: { light: 'rgba(16,185,129,0.18)', dark: 'rgba(16,185,129,0.25)' },
  partial: { light: 'rgba(245,158,11,0.18)', dark: 'rgba(245,158,11,0.25)' },
  gap: { light: 'rgba(239,68,68,0.18)', dark: 'rgba(239,68,68,0.25)' },
  holiday: { light: 'rgba(148,163,184,0.12)', dark: 'rgba(148,163,184,0.15)' },
  weekend: { light: 'rgba(148,163,184,0.06)', dark: 'rgba(148,163,184,0.08)' },
  future: { light: 'transparent', dark: 'transparent' },
};

const STATUS_BORDER: Record<CellStatus, string> = {
  complete: 'rgba(16,185,129,0.4)',
  partial: 'rgba(245,158,11,0.4)',
  gap: 'rgba(239,68,68,0.5)',
  holiday: 'rgba(148,163,184,0.2)',
  weekend: 'transparent',
  future: 'transparent',
};

interface MonthData {
  year: number;
  month: number;
  label: string;
  days: { date: string; dayOfMonth: number; dayOfWeek: number; summary?: DaySummary }[];
}

function buildMonths(from: string, to: string, days: Record<string, DaySummary>): MonthData[] {
  const result: MonthData[] = [];
  const start = new Date(from + 'T00:00:00');
  const end = new Date(to + 'T00:00:00');

  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = cur.getMonth();
    const label = cur.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const monthDays: MonthData['days'] = [];

    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(y, m, d);
      // Format as YYYY-MM-DD without toISOString() — toISOString() converts
      // to UTC which shifts the date back by 1 day in IST (UTC+5:30).
      const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (dt < start || dt > end) continue;
      const dow = dt.getDay(); // 0=Sun
      const mondayBased = dow === 0 ? 6 : dow - 1; // 0=Mon
      monthDays.push({ date: iso, dayOfMonth: d, dayOfWeek: mondayBased, summary: days[iso] });
    }
    if (monthDays.length > 0) {
      result.push({ year: y, month: m, label, days: monthDays });
    }
    cur = new Date(y, m + 1, 1);
  }
  return result;
}

export default function CalendarHeatMap() {
  const { calendarFrom, calendarTo, setCalendarRange, push } = useHftStore();
  const { data, isLoading } = useCalendar(calendarFrom, calendarTo);

  const months = useMemo(
    () => buildMonths(calendarFrom, calendarTo, data?.days || {}),
    [calendarFrom, calendarTo, data],
  );

  return (
    <Box>
      {/* Date range controls */}
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <TextField
          type="date"
          label="From"
          size="small"
          value={calendarFrom}
          onChange={(e) => setCalendarRange(e.target.value, calendarTo)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          type="date"
          label="To"
          size="small"
          value={calendarTo}
          onChange={(e) => setCalendarRange(calendarFrom, e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Legend />
      </Box>

      {isLoading && <Typography color="text.secondary">Loading...</Typography>}

      {/* Month grids */}
      <Box display="flex" flexWrap="wrap" gap={3}>
        {months.map((month) => (
          <MonthGrid key={`${month.year}-${month.month}`} month={month} onClickDay={(d) => push('day', { date: d })} />
        ))}
      </Box>
    </Box>
  );
}

function MonthGrid({ month, onClickDay }: { month: MonthData; onClickDay: (date: string) => void }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        background: (t) => (t.palette.mode === 'dark' ? 'rgba(30,41,59,0.4)' : 'rgba(241,245,249,0.6)'),
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 230,
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5} textAlign="center">
        {month.label}
      </Typography>
      {/* Weekday headers */}
      <Box display="grid" gridTemplateColumns="repeat(7, 30px)" gap="2px" justifyContent="center">
        {WEEKDAYS.map((d) => (
          <Typography key={d} variant="caption" textAlign="center" color="text.secondary" fontSize={10}>
            {d}
          </Typography>
        ))}
        {/* Leading blanks */}
        {month.days.length > 0 &&
          Array.from({ length: month.days[0].dayOfWeek }).map((_, i) => <Box key={`b-${i}`} />)}
        {/* Day cells */}
        {month.days.map((d) => {
          const status = cellStatus(d.summary, d.date);
          const clickable = status !== 'weekend' && status !== 'future';
          return (
            <Tooltip
              key={d.date}
              title={`${d.date} — ${status}${d.summary?.segments?.length ? ` (${d.summary.segments.join(', ')})` : ''}`}
              arrow
              placement="top"
            >
              <motion.div
                whileHover={clickable ? { scale: 1.2, zIndex: 10 } : {}}
                whileTap={clickable ? { scale: 0.95 } : {}}
                style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
                onClick={() => clickable && onClickDay(d.date)}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: (t) => STATUS_BG[status][t.palette.mode],
                    border: '1px solid',
                    borderColor: STATUS_BORDER[status],
                    transition: 'all 0.15s',
                  }}
                >
                  <Typography fontSize={11} fontWeight={status === 'gap' ? 700 : 400} lineHeight={1}>
                    {d.dayOfMonth}
                  </Typography>
                  {/* Segment dots */}
                  {d.summary && d.summary.segments.length > 0 && (
                    <Box display="flex" gap="1px" mt="1px">
                      {SEGMENTS.map((s) => (
                        <Box
                          key={s}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            background: d.summary!.segments.includes(s)
                              ? SEG_COLORS[s]
                              : 'transparent',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </motion.div>
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
}

function Legend() {
  const items: { label: string; color: string }[] = [
    { label: 'Complete', color: 'rgba(16,185,129,0.4)' },
    { label: 'Partial', color: 'rgba(245,158,11,0.4)' },
    { label: 'Gap', color: 'rgba(239,68,68,0.5)' },
    { label: 'Holiday', color: 'rgba(148,163,184,0.3)' },
  ];
  return (
    <Box display="flex" gap={1.5} alignItems="center" ml="auto">
      {items.map((it) => (
        <Box key={it.label} display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, background: it.color }} />
          <Typography variant="caption" color="text.secondary">
            {it.label}
          </Typography>
        </Box>
      ))}
      <Box display="flex" gap={0.5} ml={1}>
        {SEGMENTS.map((s) => (
          <Box key={s} display="flex" alignItems="center" gap={0.3}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: SEG_COLORS[s] }} />
            <Typography variant="caption" fontSize={10} color="text.secondary">
              {SEG_SHORT[s]}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
